import api from './api';
import type { Property } from '../types/property';

export const getProperties = async (
  agencySlug: string,
  page = 1,
  limit = 20,
  archivedOnly = false,
  saleType?: 'sale' | 'rental'
) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    archivedOnly: String(archivedOnly),
  });
  if (saleType) params.set('saleType', saleType);
  const res = await api.get<{ properties: Property[]; total: number }>(
    `/agencies/${agencySlug}/properties?${params.toString()}`
  );
  return res.data;
};

/** Fetch active (non-archived) properties only. Use for override picker etc. */
export const getActiveProperties = async (agencySlug: string, page = 1, limit = 200) => {
  return getProperties(agencySlug, page, limit, false);
};

export const getProperty = async (agencySlug: string, propertyId: string) => {
  const res = await api.get<Property>(`/agencies/${agencySlug}/properties/${propertyId}`);
  return res.data;
};

export const getAssignedTasks = async (agencySlug: string, propertyId: string, status?: string) => {
  const params = status ? `?status=${status}` : '';
  const res = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/assigned-tasks${params}`);
  return res.data;
};

export const updateTask = async (
  agencySlug: string,
  propertyId: string,
  workflowId: string,
  stageIndex: number,
  taskIndex: number,
  updates: { status?: string; fieldValues?: Record<string, any>; assignedTo?: string; dueDate?: string }
) => {
  const res = await api.patch(
    `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`,
    updates
  );
  return res.data;
};

export const createProperty = async (agencySlug: string, data: Partial<Property>) => {
  const res = await api.post<Property>(`/agencies/${agencySlug}/properties`, data);
  return res.data;
};

export const updateProperty = async (agencySlug: string, propertyId: string, data: Partial<Property>) => {
  const res = await api.put<Property>(`/agencies/${agencySlug}/properties/${propertyId}`, data);
  return res.data;
};

export const markPropertyAsSold = async (
  agencySlug: string,
  propertyId: string,
  data: {
    soldPrice?: number;
    purchasers: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      address?: string;
      city?: string;
      state?: string;
      postcode?: string;
      notes?: string;
    }>;
    createCustomer?: boolean;
  }
) => {
  const res = await api.post<Property>(`/agencies/${agencySlug}/properties/${propertyId}/mark-as-sold`, data);
  return res.data;
};

export const uploadPropertyImage = async (
  agencySlug: string,
  propertyId: string,
  file: File
) => {
  // 1) Ask backend for presigned PUT url
  const init = await api.post<{ url: string; key: string; uploadUrl: string }>(
    `/agencies/${agencySlug}/properties/${propertyId}/images`,
    { fileName: file.name, mimeType: file.type || 'application/octet-stream', sizeBytes: file.size }
  );
  const { uploadUrl, key, url } = init.data;
  // 2) PUT file directly to S3/MinIO
  await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file });
  // 3) Finalize to record size and update storage usage
  await api.post(`/agencies/${agencySlug}/properties/${propertyId}/uploads/finalize`, { key, sizeBytes: file.size, type: 'image' });
  return { url, key };
};

export const deletePropertyImage = async (
  agencySlug: string,
  propertyId: string,
  key: string
) => {
  const res = await api.delete(`/agencies/${agencySlug}/properties/${propertyId}/images`, { data: { key } });
  return res.data;
};

export const uploadPropertyFloorPlan = async (
  agencySlug: string,
  propertyId: string,
  file: File
) => {
  const init = await api.post<{ uploadUrl: string; url: string; key: string }>(
    `/agencies/${agencySlug}/properties/${propertyId}/floor-plan`,
    { fileName: file.name, mimeType: file.type || 'application/pdf', sizeBytes: file.size }
  );
  const { uploadUrl, key, url } = init.data;
  await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/pdf' }, body: file });
  await api.post(`/agencies/${agencySlug}/properties/${propertyId}/uploads/finalize`, { key, sizeBytes: file.size, type: 'floorPlan' });
  return { url, key };
};

export const deletePropertyFloorPlan = async (
  agencySlug: string,
  propertyId: string,
  floorPlanKey: string
) => {
  await api.delete(`/agencies/${agencySlug}/properties/${propertyId}/floor-plan/${encodeURIComponent(floorPlanKey)}`);
};

export const uploadPropertyContract = async (
  agencySlug: string,
  propertyId: string,
  file: File,
  contractType?: 'sale' | 'rental'
) => {
  const formData = new FormData();
  formData.append('file', file);
  if (contractType) {
    formData.append('contractType', contractType);
  }
  const res = await api.post<{ url: string; key: string; fileName: string }>(
    `/agencies/${agencySlug}/properties/${propertyId}/upload`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  );
  return res.data;
};

export const uploadAgencyAgreement = async (
  agencySlug: string,
  propertyId: string,
  file: File
) => {
  // 1) Ask backend for presigned PUT url
  const init = await api.post<{ uploadUrl: string; url: string; key: string }>(
    `/agencies/${agencySlug}/properties/${propertyId}/agency-agreement`,
    { fileName: file.name, mimeType: file.type || 'application/pdf', sizeBytes: file.size }
  );
  const { uploadUrl, key, url } = init.data;
  
  // 2) PUT file directly to S3/MinIO
  await fetch(uploadUrl, { 
    method: 'PUT', 
    headers: { 'Content-Type': file.type || 'application/pdf' }, 
    body: file 
  });
  
  // 3) Finalize to record size and update storage usage
  await api.post(`/agencies/${agencySlug}/properties/${propertyId}/uploads/finalize`, { 
    key, 
    sizeBytes: file.size, 
    type: 'agencyAgreement' 
  });
  
  return { url, key };
};

export const uploadRentalManagementAgreement = async (
  agencySlug: string,
  propertyId: string,
  file: File
) => {
  // 1) Ask backend for presigned PUT url
  const init = await api.post<{ uploadUrl: string; url: string; key: string }>(
    `/agencies/${agencySlug}/properties/${propertyId}/rental-management-agreement`,
    { fileName: file.name, mimeType: file.type || 'application/pdf', sizeBytes: file.size }
  );
  const { uploadUrl, key, url } = init.data;
  
  // 2) PUT file directly to S3/MinIO
  await fetch(uploadUrl, { 
    method: 'PUT', 
    headers: { 'Content-Type': file.type || 'application/pdf' }, 
    body: file 
  });
  
  // 3) Finalize to record size and update storage usage
  await api.post(`/agencies/${agencySlug}/properties/${propertyId}/uploads/finalize`, { 
    key, 
    sizeBytes: file.size, 
    type: 'agencyAgreement' // Use same type for storage tracking
  });
  
  return { url, key };
};

export const deleteProperty = async (agencySlug: string, propertyId: string) => {
  const res = await api.delete<{ message: string }>(`/agencies/${agencySlug}/properties/${propertyId}`);
  return res.data;
};

export const updateStylingCompany = async (agencySlug: string, propertyId: string, stylingCompanyData: {
  name: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  website?: string;
  notes?: string;
}) => {
  const res = await api.put<Property>(`/agencies/${agencySlug}/properties/${propertyId}/styling-company`, stylingCompanyData);
  return res.data;
};

export const updateAuctionDetails = async (agencySlug: string, propertyId: string, auctionDetails: {
  date?: string;
  time?: string;
  location?: string;
  reservePrice?: number;
  auctioneer?: string;
  notes?: string;
}) => {
  const res = await api.put<Property>(`/agencies/${agencySlug}/properties/${propertyId}/auction-details`, auctionDetails);
  return res.data;
};

export const updateAuctionOutcome = async (
  agencySlug: string,
  propertyId: string,
  outcome: {
    result?:
      | 'sold-prior-to-auction'
      | 'sold-at-auction'
      | 'passed-in'
      | 'passed-in-vendor-bid'
      | 'withdrawn'
      | 'sold-after-auction';
    date?: string;
    maxBid?: number;
    notes?: string;
  }
) => {
  const res = await api.put<Property>(
    `/agencies/${agencySlug}/properties/${propertyId}/auction-outcome`,
    outcome
  );
  return res.data;
};

export const updateSoldDetails = async (
  agencySlug: string,
  propertyId: string,
  soldDetails: {
    soldDate?: string;
    soldPrice?: number;
    showSoldPrice?: boolean;
    source?: 'auction' | 'private_treaty' | 'other';
    notes?: string;
  }
) => {
  const res = await api.put<Property>(
    `/agencies/${agencySlug}/properties/${propertyId}/sold-details`,
    soldDetails
  );
  return res.data;
};

export interface PropertyAttendee {
  _id?: string;
  name: string;
  email?: string;
  phone: string;
  notes?: string;
  recordedAt: string;
  recordedBy?: string;
  recordedByUser?: {
    name: string;
    email: string;
  };
  visitDate?: string;
  visitType?: 'viewing' | 'inspection' | 'open_house' | 'private_viewing' | 'other';
  feedback?: string;
  interestLevel?: 'high' | 'medium' | 'low' | 'none';
  priceFeedback?: string;
  followUpIntent?: 'wants_contract' | 'wants_second_inspection' | 'considering' | 'not_interested' | 'no_follow_up';
  objections?: string;
  keenBuyer?: boolean;
  attendeeRequestsContract?: boolean;
  contractRequested?: boolean;
  contractSent?: boolean;
  contractSentAt?: string;
  attendeeRequestsApplicationForm?: boolean;
  applicationFormSent?: boolean;
  applicationFormSentAt?: string;
  tags?: string[];
}

export const getPropertyAttendees = async (agencySlug: string, propertyId: string): Promise<PropertyAttendee[]> => {
  const res = await api.get<{ success: boolean; data: PropertyAttendee[] }>(
    `/agencies/${agencySlug}/properties/${propertyId}/attendees`
  );
  return res.data.data;
};

export const addPropertyAttendee = async (
  agencySlug: string,
  propertyId: string,
  attendee: {
    name: string;
    email?: string;
    phone: string;
    notes?: string;
    visitDate?: string;
    visitType?: 'viewing' | 'inspection' | 'open_house' | 'private_viewing' | 'other';
    feedback?: string;
    interestLevel?: 'high' | 'medium' | 'low' | 'none';
    priceFeedback?: string;
    followUpIntent?: 'wants_contract' | 'wants_second_inspection' | 'considering' | 'not_interested' | 'no_follow_up';
    objections?: string;
    attendeeRequestsContract?: boolean;
    attendeeRequestsApplicationForm?: boolean;
    tags?: string[];
  }
): Promise<PropertyAttendee> => {
  const res = await api.post<{ success: boolean; data: PropertyAttendee }>(
    `/agencies/${agencySlug}/properties/${propertyId}/attendees`,
    attendee
  );
  return res.data.data;
};

export const updatePropertyAttendee = async (
  agencySlug: string,
  propertyId: string,
  attendeeId: string,
  attendee: {
    name: string;
    email?: string;
    phone: string;
    notes?: string;
    visitDate?: string;
    visitType?: 'viewing' | 'inspection' | 'open_house' | 'private_viewing' | 'other';
    feedback?: string;
    interestLevel?: 'high' | 'medium' | 'low' | 'none';
    priceFeedback?: string;
    followUpIntent?: 'wants_contract' | 'wants_second_inspection' | 'considering' | 'not_interested' | 'no_follow_up';
    objections?: string;
    attendeeRequestsContract?: boolean;
    attendeeRequestsApplicationForm?: boolean;
    tags?: string[];
  }
): Promise<PropertyAttendee> => {
  const res = await api.put<{ success: boolean; data: PropertyAttendee }>(
    `/agencies/${agencySlug}/properties/${propertyId}/attendees/${attendeeId}`,
    attendee
  );
  return res.data.data;
};

export const deletePropertyAttendee = async (
  agencySlug: string,
  propertyId: string,
  attendeeId: string
): Promise<void> => {
  await api.delete(`/agencies/${agencySlug}/properties/${propertyId}/attendees/${attendeeId}`);
};

export interface VisitorInspectionReportAttendee extends PropertyAttendee {
  attendeeRequestsContract?: boolean;
  attendeeRequestsApplicationForm?: boolean;
}

export interface VisitorInspectionReport {
  propertyId: string;
  propertyAddress: string;
  agencyName: string;
  status: 'draft' | 'ready' | 'finalized';
  summary: string;
  agentNotes?: string;
  includedAttendeeIds: string[];
  periodStart: string;
  periodEnd: string;
  vendorVisibleContactDetails: boolean;
  vendorEmails?: string[];
  vendorAccessCode?: string;
  vendorAccessToken?: string;
  sentAt?: string;
  finalizedAt?: string;
  metrics: {
    totalVisitors: number;
    feedbackCount: number;
    highInterestCount: number;
    contractRequestCount: number;
    applicationRequestCount: number;
    sessionCount: number;
  };
  sessions: Array<{
    _id: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    status?: string;
    attendeeCount: number;
  }>;
  attendees: VisitorInspectionReportAttendee[];
}

export const getVisitorInspectionReport = async (
  agencySlug: string,
  propertyId: string,
  period?: Pick<VisitorInspectionReport, 'periodStart' | 'periodEnd'>
): Promise<VisitorInspectionReport> => {
  const res = await api.get<{ success: boolean; data: VisitorInspectionReport }>(
    `/agencies/${agencySlug}/properties/${propertyId}/visitor-inspection-report`,
    { params: period }
  );
  return res.data.data;
};

type VisitorInspectionReportUpdatePayload = Partial<
  Pick<VisitorInspectionReport, 'summary' | 'agentNotes' | 'includedAttendeeIds' | 'vendorVisibleContactDetails'>
> & Pick<VisitorInspectionReport, 'periodStart' | 'periodEnd'>;

export const updateVisitorInspectionReport = async (
  agencySlug: string,
  propertyId: string,
  payload: VisitorInspectionReportUpdatePayload
): Promise<VisitorInspectionReport> => {
  const res = await api.put<{ success: boolean; data: VisitorInspectionReport }>(
    `/agencies/${agencySlug}/properties/${propertyId}/visitor-inspection-report`,
    payload
  );
  return res.data.data;
};

export const generateVisitorInspectionReport = async (
  agencySlug: string,
  propertyId: string,
  payload: Pick<VisitorInspectionReport, 'periodStart' | 'periodEnd'>
): Promise<VisitorInspectionReport> => {
  const res = await api.post<{ success: boolean; data: VisitorInspectionReport }>(
    `/agencies/${agencySlug}/properties/${propertyId}/visitor-inspection-report/generate`,
    payload
  );
  return res.data.data;
};

export const finalizeVisitorInspectionReport = async (
  agencySlug: string,
  propertyId: string,
  payload: VisitorInspectionReportUpdatePayload
): Promise<VisitorInspectionReport> => {
  const res = await api.post<{ success: boolean; data: VisitorInspectionReport }>(
    `/agencies/${agencySlug}/properties/${propertyId}/visitor-inspection-report/finalize`,
    payload
  );
  return res.data.data;
};

export const sendVisitorInspectionReportEmail = async (
  agencySlug: string,
  propertyId: string,
  emails: string[]
): Promise<{ message: string; vendorLink: string; vendorLinkByCode: string; vendorAccessCode: string; sentTo: string[] }> => {
  const res = await api.post(
    `/agencies/${agencySlug}/properties/${propertyId}/visitor-inspection-report/send-email`,
    { emails }
  );
  return res.data;
};

export const suggestVisitorInspectionReportSummary = async (
  agencySlug: string,
  propertyId: string,
  payload: Pick<VisitorInspectionReport, 'periodStart' | 'periodEnd'>
): Promise<{ suggestedSummary: string }> => {
  const res = await api.post<{ success: boolean; suggestedSummary: string }>(
    `/agencies/${agencySlug}/properties/${propertyId}/visitor-inspection-report/suggest-summary`,
    payload
  );
  return { suggestedSummary: res.data.suggestedSummary };
};

export const requestContract = async (
  agencySlug: string,
  propertyId: string,
  attendeeId: string
): Promise<PropertyAttendee> => {
  const res = await api.put<{ success: boolean; data: PropertyAttendee }>(
    `/agencies/${agencySlug}/properties/${propertyId}/attendees/${attendeeId}/request-contract`
  );
  return res.data.data;
};

export const sendContract = async (
  agencySlug: string,
  propertyId: string,
  attendeeId: string
): Promise<PropertyAttendee> => {
  const res = await api.put<{ success: boolean; data: PropertyAttendee }>(
    `/agencies/${agencySlug}/properties/${propertyId}/attendees/${attendeeId}/send-contract`
  );
  return res.data.data;
};

export const resendContract = async (
  agencySlug: string,
  propertyId: string,
  attendeeId: string
): Promise<PropertyAttendee> => {
  const res = await api.put<{ success: boolean; data: PropertyAttendee }>(
    `/agencies/${agencySlug}/properties/${propertyId}/attendees/${attendeeId}/resend-contract`
  );
  return res.data.data;
};

export const sendApplicationForm = async (
  agencySlug: string,
  propertyId: string,
  attendeeId: string
): Promise<PropertyAttendee> => {
  const res = await api.put<{ success: boolean; data: PropertyAttendee }>(
    `/agencies/${agencySlug}/properties/${propertyId}/attendees/${attendeeId}/send-application-form`
  );
  return res.data.data;
};

export interface SearchAttendeeResult {
  name: string;
  email?: string;
  phone?: string;
  tags?: string[];
}

export const publishPropertyToListings = async (agencySlug: string, propertyId: string) => {
  const res = await api.post<{
    success: boolean;
    jobId: string;
    message: string;
    isUpdate: boolean;
    platforms: {
      realestate: boolean;
      domain: boolean;
    };
  }>(`/agencies/${agencySlug}/properties/${propertyId}/publish-listings`);
  return res.data;
};

export const searchAttendees = async (
  agencySlug: string,
  query: string
): Promise<SearchAttendeeResult[]> => {
  if (!query || query.trim().length < 2) {
    return [];
  }
  const res = await api.get<{ success: boolean; data: SearchAttendeeResult[] }>(
    `/agencies/${agencySlug}/attendees/search?q=${encodeURIComponent(query)}`
  );
  return res.data.data;
}; 